import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

const TermsOfServicePage = () => {
  const [expandedSections, setExpandedSections] = useState({
    section1: true,
    section2: false,
    section3: false,
    section4: false,
    section5: false,
    section6: false,
    section7: false,
    section8: false,
    section9: false,
    section10: false,
    section11: false,
    section12: false
  });

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  return (
    <>
      <Helmet>
        <title>Terms of Service | VEDUS Supply Chain Management</title>
        <meta name="description" content="Terms of Service for VEDUS Supply Chain Management services." />
      </Helmet>

      <div className="container mx-auto px-4 py-12">
        <motion.h1 
          className="text-3xl font-bold mb-2 text-secondary-900 dark:text-white text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Terms of Service
        </motion.h1>
        
        <motion.p 
          className="text-center mb-8 text-secondary-600 dark:text-secondary-300"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Last Updated: {new Date().toLocaleDateString()}
        </motion.p>
        
        <motion.div 
          className="max-w-3xl mx-auto bg-white dark:bg-secondary-800 rounded-lg shadow-md overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="p-6">
            <p className="text-secondary-600 dark:text-secondary-300 mb-6">
              This document outlines the terms and conditions for using VEDUS Supply Chain Management services. 
              Please read carefully to understand your rights and responsibilities.
            </p>
            
            {/* Section 1 */}
            <div className="border-b border-secondary-200 dark:border-secondary-700 pb-4 mb-4">
              <button 
                className="flex justify-between items-center w-full text-left font-semibold text-lg text-secondary-900 dark:text-white"
                onClick={() => toggleSection('section1')}
              >
                <span>1. Acceptance of Terms</span>
                {expandedSections.section1 ? <FaChevronUp /> : <FaChevronDown />}
              </button>
              
              {expandedSections.section1 && (
                <div className="mt-3 text-secondary-600 dark:text-secondary-300 pl-4">
                  <p>
                    By accessing or using the services provided by VEDUS Supply Chain Management Pvt. Ltd. ("VEDUS," "we," "us," or "our"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
                  </p>
                </div>
              )}
            </div>
            
            {/* Section 2 */}
            <div className="border-b border-secondary-200 dark:border-secondary-700 pb-4 mb-4">
              <button 
                className="flex justify-between items-center w-full text-left font-semibold text-lg text-secondary-900 dark:text-white"
                onClick={() => toggleSection('section2')}
              >
                <span>2. Description of Services</span>
                {expandedSections.section2 ? <FaChevronUp /> : <FaChevronDown />}
              </button>
              
              {expandedSections.section2 && (
                <div className="mt-3 text-secondary-600 dark:text-secondary-300 pl-4">
                  <p>
                    VEDUS provides logistics and supply chain management services, including but not limited to road transport, air freight, rail transport, sea freight, domestic shipping, and international shipping.
                  </p>
                </div>
              )}
            </div>
            
            {/* Section 3 */}
            <div className="border-b border-secondary-200 dark:border-secondary-700 pb-4 mb-4">
              <button 
                className="flex justify-between items-center w-full text-left font-semibold text-lg text-secondary-900 dark:text-white"
                onClick={() => toggleSection('section3')}
              >
                <span>3. User Responsibilities</span>
                {expandedSections.section3 ? <FaChevronUp /> : <FaChevronDown />}
              </button>
              
              {expandedSections.section3 && (
                <div className="mt-3 text-secondary-600 dark:text-secondary-300 pl-4">
                  <p>When using our services, you agree to:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Provide accurate and complete information about shipments</li>
                    <li>Comply with all applicable laws and regulations</li>
                    <li>Not ship prohibited or dangerous items without proper declaration</li>
                    <li>Pay all fees and charges associated with our services</li>
                    <li>Maintain the confidentiality of any account credentials</li>
                  </ul>
                </div>
              )}
            </div>
            
            {/* Continue with remaining sections */}
            <div className="border-b border-secondary-200 dark:border-secondary-700 pb-4 mb-4">
              <button 
                className="flex justify-between items-center w-full text-left font-semibold text-lg text-secondary-900 dark:text-white"
                onClick={() => toggleSection('section4')}
              >
                <span>4. Service Limitations</span>
                {expandedSections.section4 ? <FaChevronUp /> : <FaChevronDown />}
              </button>
              
              {expandedSections.section4 && (
                <div className="mt-3 text-secondary-600 dark:text-secondary-300 pl-4">
                  <p>VEDUS strives to provide reliable services, but we cannot guarantee:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Exact delivery times, which may be affected by factors beyond our control</li>
                    <li>Service availability in all regions or during extreme weather conditions</li>
                    <li>Uninterrupted access to our online tracking or other digital services</li>
                  </ul>
                </div>
              )}
            </div>
            
            {/* Additional sections follow the same pattern */}
            {/* Section 5-12 would be implemented similarly */}
            
            <div className="mt-6 text-center">
              <p className="text-secondary-600 dark:text-secondary-300">
                If you have any questions about our Terms of Service, please <a href="/contact" className="text-primary-600 dark:text-primary-400 hover:underline">contact us</a>.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default TermsOfServicePage;